"use client";

import { useState } from "react";
import Link from "next/link";
import { Menu, Search, User, ShoppingBag, Truck } from "lucide-react";
import Container from "@/components/ui/container";
import IconButton from "@/components/ui/icon-button";
import TopBar from "@/components/layout/top-bar";
import CategoryTabs from "@/components/layout/category-tabs";
import MobileNav from "@/components/layout/mobile-nav";
import MiniCart from "@/components/layout/mini-cart";
import { siteContent } from "@/lib/data";

// Visual-only basket count until cart is wired.
const MOCK_BASKET_COUNT = 2;

export default function Header() {
  const [navOpen, setNavOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const phoneRaw = siteContent.brand.phone.replace(/\s+/g, "");

  return (
    <>
      <header className="sticky top-0 z-30 bg-[color:var(--color-surface-alt)] backdrop-blur border-b border-[color:var(--color-border)]">
        <TopBar />
        <Container className="flex h-16 lg:h-24 items-center gap-3 lg:gap-6">
          <IconButton
            label="Open menu"
            onClick={() => setNavOpen(true)}
            className="lg:hidden"
          >
            <Menu size={22} />
          </IconButton>

          <Link
            href="/"
            aria-label={siteContent.brand.name}
            className="shrink-0 text-2xl lg:text-[1.75rem] leading-none font-semibold tracking-tight mx-auto lg:mx-0"
          >
            <span className="text-[color:var(--color-text)]">tile</span>
            <span className="text-[color:var(--color-primary)]">fort</span>
          </Link>

          {/* Desktop: search centered */}
          <div className="hidden lg:flex flex-1 justify-center">
            <form
              role="search"
              action="/collections"
              className="flex w-full max-w-[32rem] items-center h-11 rounded-[var(--radius-pill)] border border-[color:var(--color-border-strong)] bg-[color:var(--color-surface)] focus-within:border-[color:var(--color-primary)]"
            >
              <Search size={18} className="ml-4 text-[color:var(--color-muted)]" />
              <label htmlFor="header-search" className="sr-only">Search tiles</label>
              <input
                id="header-search"
                name="q"
                type="search"
                placeholder="Search"
                className="flex-1 min-w-0 bg-transparent px-3 text-sm outline-none placeholder:text-[color:var(--color-muted)]"
              />
            </form>
          </div>

          {/* Mobile icons only */}
          <div className="ml-auto flex items-center gap-1 lg:hidden">
            <IconButton label="Search">
              <Search size={20} />
            </IconButton>
            <IconButton label="Open basket" onClick={() => setCartOpen(true)}>
              <ShoppingBag size={20} />
            </IconButton>
          </div>

          {/* Desktop: phone block + Track Order / Account / Basket, pinned right */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-7 shrink-0">
            <a
              href={`tel:${phoneRaw}`}
              className="flex flex-col leading-tight group"
            >
              <span className="text-[11px] text-[color:var(--color-muted)]">
                For sales &amp; advice call
              </span>
              <span className="text-base font-bold text-[color:var(--color-text)] group-hover:text-[color:var(--color-primary)]">
                {siteContent.brand.phone}
              </span>
              <span className="text-[11px] text-[color:var(--color-muted)]">
                Mon&ndash;Fri 8am&ndash;6pm
              </span>
            </a>

            <Link
              href="/contact"
              className="flex flex-col items-center gap-1 text-[color:var(--color-text)] hover:text-[color:var(--color-primary)]"
            >
              <Truck size={26} strokeWidth={1.5} />
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">
                Track Order
              </span>
            </Link>

            <Link
              href="/contact"
              className="flex flex-col items-center gap-1 text-[color:var(--color-text)] hover:text-[color:var(--color-primary)]"
            >
              <User size={26} strokeWidth={1.5} />
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">
                Account
              </span>
            </Link>

            <button
              type="button"
              onClick={() => setCartOpen(true)}
              aria-label="Open basket"
              className="relative flex flex-col items-center gap-1 text-[color:var(--color-text)] hover:text-[color:var(--color-primary)]"
            >
              <span className="relative">
                <ShoppingBag size={26} strokeWidth={1.5} />
                {MOCK_BASKET_COUNT > 0 && (
                  <span className="absolute -right-2 -top-1.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[color:var(--color-accent)] px-1 text-[11px] font-bold text-[color:var(--color-secondary)]">
                    {MOCK_BASKET_COUNT}
                  </span>
                )}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">
                Basket
              </span>
            </button>
          </div>
        </Container>
        <CategoryTabs />
      </header>

      <MobileNav open={navOpen} onClose={() => setNavOpen(false)} />
      <MiniCart open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
}
